import gql from "graphql-tag";
import {useCallback} from "react";

import {useMutation} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {Errors, KAError} from "@khan/logging";

import type {CloseThreadType} from "./__graphql-types__/index.ts";

export const CLOSE_THREAD_MUTATION: GqlOperation<
    CloseThreadType["response"],
    CloseThreadType["variables"]
> = gqlOp(gql`
    mutation CloseThread($input: String!) {
        closeThread(threadId: $input) {
            error {
                code
            }
        }
    }
`);

/**
 * Hook to produce a function for closing threads.
 *
 * This hook returns a function that can be invoked to close
 * a thread.
 *
 * @returns A function that takes an identifier for the thread
 * to be stopped. When invoked, this function sends a request to
 * to close the given thread, and returns a promise
 * that resolves when the request is complete.
 */
export const useCloseThreadMutation = (): ((
    threadId: string,
) => Promise<void>) => {
    const [mutate] = useMutation(CLOSE_THREAD_MUTATION);
    const closeThreadMutation = useCallback(
        async (threadId: string) => {
            const result = await mutate({input: threadId});
            // If we have an error status or we got a successful response but
            // it has an error in it, then we throw an error.
            // We should never see the loading status here since this waits
            // for the mutation to be complete, and we the aborted status is
            // something we can ignore for this case.
            const error =
                result.status === "error"
                    ? result.error
                    : result.status === "success" &&
                        result.data.closeThread.error
                      ? result.data.closeThread.error.code
                      : null;
            if (error) {
                throw new KAError(
                    "Failed to record AI Guide close thread mutation",
                    Errors.Internal,
                    {cause: error},
                );
            }
        },
        [mutate],
    );
    return closeThreadMutation;
};
